import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useSite } from '../../context/SiteProvider.jsx';
import { useCart } from '../../context/CartProvider.jsx';

/**
 * Navigation entries are stored as either in-page anchors (`#catalog`) or real
 * routes (`/shop`). Anchors only work on the homepage, so from any other route
 * they become `/#catalog` and the browser handles the scroll after navigation.
 */
function NavLink({ link, onNavigate }) {
  const { t } = useSite();
  const location = useLocation();
  const isAnchor = link.href.startsWith('#');
  const target = isAnchor && location.pathname !== '/' ? `/${link.href}` : link.href;

  // On the homepage itself, scroll directly rather than relying on the hash
  // "changing" — clicking the same nav item twice in a row should still jump
  // back to that section, even though the URL hash stays identical.
  const handleClick = (event) => {
    onNavigate?.();
    if (isAnchor && location.pathname === '/') {
      const id = link.href.slice(1);
      const el = document.getElementById(id);
      if (el) {
        event.preventDefault();
        window.history.replaceState(null, '', `#${id}`);
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <Link to={target} onClick={handleClick}>
      {t(link.labelKey)}
    </Link>
  );
}

export function Topbar() {
  const { t, settings, language, toggleLanguage } = useSite();
  if (settings['ui.show_topbar'] === false) return null;

  return (
    <div className="topbar">
      <p>{t('topbar')}</p>
      <button id="languageToggle" type="button" aria-label="Switch website language" onClick={toggleLanguage}>
        {language === 'en' ? 'العربية' : 'English'}
      </button>
    </div>
  );
}

export function Header() {
  const { t, settings, nav, href } = useSite();
  const cart = useCart();
  const navigate = useNavigate();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenu = () => setIsMenuOpen(false);

  const whatsapp = href('{{whatsapp}}');
  const phone = href('tel:{{phone}}');

  return (
    <header className={`header${isScrolled ? ' scrolled' : ''}`}>
      <Link className="brand brand-logo" to="/" aria-label="Connix home" onClick={closeMenu}>
        <img src={settings['brand.logo']} alt={settings['brand.name'] ?? 'Connix'} />
      </Link>

      <nav id="nav" className={`nav${isMenuOpen ? ' open' : ''}`} aria-label="Main navigation">
        {nav.header.map((link) => (
          <NavLink key={link.id} link={link} onNavigate={closeMenu} />
        ))}
        <div className="nav-mobile-actions">
          <a className="call-link" href={phone}>{t('callNow')}</a>
          <a className="whatsapp-button small" href={whatsapp} target="_blank" rel="noopener">
            <span className="wa-dot">●</span>
            <span>{t('whatsapp')}</span>
          </a>
        </div>
      </nav>

      <div className="header-actions">
        <button
          id="cartOpenButton"
          className="header-cart"
          type="button"
          onClick={() => {
            closeMenu();
            cart.open();
          }}
        >
          <span>{t('cart')}</span>
          <b id="cartCount">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            <span>{cart.itemCount}</span>
          </b>
        </button>
        <a className="call-link" href={phone}>{t('callNow')}</a>
        <a className="whatsapp-button small" href={whatsapp} target="_blank" rel="noopener">
          <span className="wa-dot">●</span>
          <span>{t('whatsapp')}</span>
        </a>
        <button
          id="menuButton"
          className={`menu-button${isMenuOpen ? ' open' : ''}`}
          type="button"
          aria-expanded={isMenuOpen}
          aria-controls="nav"
          aria-label="Open menu"
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          <span />
          <span />
        </button>
      </div>
    </header>
  );
}

export function Footer() {
  const { t, settings, nav, block } = useSite();
  const footerBlock = block('footer');
  const settingsData = footerBlock?.settings ?? {};

  return (
    <footer className="footer">
      <div className="footer-brand">
        <div className="footer-logo">
          <img src={settingsData.logo ?? settings['brand.logo']} alt={settings['brand.name'] ?? 'Connix'} />
        </div>
        <p>{t(settingsData.lineKey ?? 'footerLine')}</p>
      </div>

      <nav className="footer-nav" aria-label="Footer navigation">
        {nav.footer.map((link) => (
          <NavLink key={link.id} link={link} />
        ))}
      </nav>

      <div className="footer-contact">
        <span>{t(settingsData.phoneLabelKey ?? 'phoneLabel')}</span>
        <div className="footer-contact-phones">
          <a href={`tel:${settings['contact.phone'] ?? ''}`}>{settings['contact.phone_display']}</a>
          {settings['contact.phone_secondary_display'] ? (
            <a href={`tel:${settings['contact.phone_secondary'] ?? ''}`}>
              {settings['contact.phone_secondary_display']}
            </a>
          ) : null}
        </div>
        <span>{t(settingsData.addressLabelKey ?? 'addressLabel')}</span>
        <p>{t(settings['contact.address_key'] ?? 'address')}</p>
      </div>

      <small className="footer-copy">© {new Date().getFullYear()} {settings['brand.name'] ?? 'CONNIX'}</small>
    </footer>
  );
}

export function FloatingActions() {
  const { settings, href } = useSite();
  if (settings['ui.show_floating_actions'] === false) return null;

  return (
    <div className="floating-actions">
      <a href={href('tel:{{phone}}')} aria-label="Call Connix">☎</a>
      <a
        className="floating-wa"
        href={href('{{whatsapp}}')}
        target="_blank"
        rel="noopener"
        aria-label="WhatsApp Connix"
      >
        <svg width="24" height="24" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
          <path d="M16.004 3C9.377 3 4 8.373 4 15c0 2.383.7 4.6 1.906 6.463L4 29l7.72-1.874A11.94 11.94 0 0 0 16.004 27C22.63 27 28 21.627 28 15S22.63 3 16.004 3Zm6.98 16.98c-.297.836-1.47 1.53-2.408 1.73-.64.136-1.474.244-4.287-.92-3.598-1.49-5.91-5.14-6.09-5.38-.177-.24-1.454-1.936-1.454-3.693 0-1.757.917-2.62 1.243-2.98.297-.328.647-.41.863-.41.216 0 .432.002.622.012.2.01.468-.076.732.559.297.72.988 2.42 1.075 2.596.087.176.146.383.03.62-.117.238-.176.386-.35.594-.176.208-.37.463-.528.622-.176.176-.36.367-.155.72.206.353.914 1.51 1.965 2.446 1.35 1.204 2.49 1.577 2.844 1.753.353.176.559.146.766-.088.206-.234.883-1.03 1.118-1.383.235-.354.47-.294.792-.176.323.117 2.045.965 2.396 1.14.353.176.588.264.676.412.088.147.088.853-.208 1.688Z" />
        </svg>
      </a>
    </div>
  );
}
